import React, { useMemo } from 'react';
import { useScopedI18n } from '@locales/client';
import { OffersGrid } from './styles';
import { WelcomeOfferBlock } from '../welcome-offer-block';
import { useBonuses } from '@/entities/bonus/model/use-bonuses';
import { useCurrencySymbol } from '@/shared/hooks/useCurrencySymbol';
import { useTranslations } from '@/shared/ui/selects/select-options/hooks';
import { fillTemplate } from '../../utils/interpolate-template';

interface WelcomeBonusOption {
  minDeposit?: number;
  text?: string;
}

interface WelcomeBonus {
  category?: string;
  depositCount?: number | string;
  options?: WelcomeBonusOption[];
}

export const WelcomeOfferList = () => {
  const { translations } = useTranslations();
  const tBonus = useScopedI18n('bonus');
  const { data: bonusData } = useBonuses();
  const currencySymbol = useCurrencySymbol();

  const parts = translations?.bonus?.page_content?.welcome_package_offer?.parts;

  const welcomeOffers = useMemo(() => {
    if (!Array.isArray(parts) || parts.length === 0) return [];

    const depositsByCount = (bonusData || []).reduce(
      (map: Record<number, WelcomeBonusOption[]>, bonus: WelcomeBonus) => {
        if (bonus?.category !== 'welcome') return map;
        const depositCount =
          bonus.depositCount !== undefined ? +bonus.depositCount : 0;
        map[depositCount] = bonus.options || [];
        return map;
      },
      {}
    );

    const lastIndex = parts.length - 1;

    return parts.map((part, index) => {
      const isWagersStep = index === lastIndex;

      if (isWagersStep) {
        return {
          stepNumber: null,
          title: part?.template,
          items: part?.list || [],
          itemsType: 'list' as const,
        };
      }

      const deposits = depositsByCount[index] || [];
      const hasDeposits = deposits.length > 0;

      return {
        stepNumber: index + 1,
        title: part?.template,
        items: !hasDeposits
          ? tBonus('bonus_received')
          : deposits.map((option: WelcomeBonusOption, depositIdx: number) =>
              fillTemplate(part?.list?.[depositIdx], {
                amount: option?.minDeposit || undefined,
                currency: currencySymbol || undefined,
                bonusAmount: option?.text?.replace(/^[^\d]*/, '') || undefined,
              })
            ),
        itemsType: hasDeposits ? ('list' as const) : ('text' as const),
      };
    });
  }, [parts, bonusData, tBonus, currencySymbol]);

  if (welcomeOffers.length === 0) return null;

  return (
    <OffersGrid>
      {welcomeOffers.map((offer, index) => (
        <WelcomeOfferBlock
          key={index}
          stepNumber={offer.stepNumber}
          title={offer.title}
          items={offer.items}
          itemsType={offer.itemsType}
        />
      ))}
    </OffersGrid>
  );
};
